// src/components/pdf-viewer/PdfViewer.js - With dynamic scaling
import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { 
  DndContext, 
  useDraggable, 
  MouseSensor, 
  TouchSensor, 
  useSensor, 
  useSensors 
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import Stamp, { STAMP_DIMENSIONS } from '../stamp/Stamp';
import PdfSkeleton from './PdfSkeleton';
import { 
  pdfPointsToPercentages,
  getDefaultStampLocationPercent,
} from '../../utils/PdfPositionUtils';
import PageNumber from './PageNumber';
import { useAuth } from '../../firebase/AuthContext';
import { incrementActionCount } from '../../hooks/useGuide';

// Configure PDF.js worker - use local worker from npm package instead of CDN for security
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

// Letter-size aspect ratio (height / width)
const LETTER_ASPECT_RATIO = 11 / 8.5;

// Minimum PDF display dimensions
const MIN_PDF_WIDTH = 200;
const MIN_PDF_HEIGHT = MIN_PDF_WIDTH * LETTER_ASPECT_RATIO; // ~517px

const DraggableStamp = React.memo(({ 
  id, 
  basePosition, 
  stampConfig, 
  documentIndex, 
  caseData, 
  onCommitPosition,
  pdfFile,
  pdfDisplayHeight
}) => {
  const stampRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: id
  });

  // Calculate the PDF's scale factor
  const pdfScale = useMemo(() => {
    if (!pdfFile?.pdfDimensions?.height) {
      console.warn('⚠️ No PDF dimensions available, using scale 1');
      return 1;
    }
    
    const scale = pdfDisplayHeight / pdfFile.pdfDimensions.height;
    
    console.log('📏 PDF scale calculation:', {
      displayHeight: pdfDisplayHeight,
      actualHeight: pdfFile.pdfDimensions.height,
      scale: scale.toFixed(3)
    });
    
    return scale;
  }, [pdfDisplayHeight, pdfFile?.pdfDimensions?.height]);

  // Calculate ACTUAL pixel dimensions for the stamp at this scale
  const stampPixelDimensions = useMemo(() => {
    return {
      width: STAMP_DIMENSIONS.BASE_WIDTH * pdfScale,
      height: STAMP_DIMENSIONS.BASE_HEIGHT * pdfScale
    };
  }, [pdfScale]);

  // Style for the draggable wrapper - ACTUAL dimensions for dnd-kit
  const wrapperStyle = useMemo(() => {
    let transformValue = '';
    
    // Only add translate transform if dragging
    if (transform) {
      transformValue = `translate(${transform.x}px, ${transform.y}px)`;
    }
    
    const ringColor = (isDragging || isHovered) ? '#0C141D' : '#8F99A3';

    return {
      position: 'absolute',
      left: `${basePosition.x}%`,
      top: `${basePosition.y}%`,
      // CRITICAL: Set actual pixel dimensions for dnd-kit
      width: `${stampPixelDimensions.width}px`,
      height: `${stampPixelDimensions.height}px`,
      borderRadius: `${STAMP_DIMENSIONS.BORDER_RADIUS * pdfScale}px`,
      transform: transformValue,
      overflow: 'hidden', // Clip the scaled content
      outline: `1px dashed ${ringColor}`,
      outlineOffset: '3px',
      willChange: 'transform',
      touchAction: 'none',
      pointerEvents: 'auto',
      cursor: isDragging ? 'grabbing' : 'grab',
      zIndex: isDragging ? 50 : 10,
      opacity: isDragging ? 0.9 : 1
    };
  }, [basePosition.x, basePosition.y, transform, stampPixelDimensions, isDragging, isHovered, pdfScale]);

  // Style for the inner stamp - scales uniformly
  const stampInnerStyle = useMemo(() => {
    return {
      transform: `scale(${pdfScale})`,
      transformOrigin: '0 0', // Scale from top-left corner
      width: `${STAMP_DIMENSIONS.BASE_WIDTH}px`,
      height: `${STAMP_DIMENSIONS.BASE_HEIGHT}px`
    };
  }, [pdfScale]);

  // Commit drag position callback
  const commitPosition = useCallback(() => {
    if (!stampRef.current) return;
    
    const stampRect = stampRef.current.getBoundingClientRect();
    const container = stampRef.current.closest('.stamp-container');
    
    if (!container) {
      console.error('Could not find .stamp-container for position calculation');
      return;
    }
    
    const containerRect = container.getBoundingClientRect();
    
    // Calculate new percentage position
    const newPercentX = ((stampRect.left - containerRect.left) / containerRect.width) * 100;
    const newPercentY = ((stampRect.top - containerRect.top) / containerRect.height) * 100;
    
    console.log('📍 Committing drag position:', {
      oldPercent: { x: basePosition.x.toFixed(2), y: basePosition.y.toFixed(2) },
      newPercent: { x: newPercentX.toFixed(2), y: newPercentY.toFixed(2) },
      stampDimensions: stampPixelDimensions
    });
    
    onCommitPosition({ x: newPercentX, y: newPercentY });
  }, [basePosition, onCommitPosition, stampPixelDimensions]);

  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        stampRef.current = node;
      }}
      className="draggable-stamp-wrapper"
      style={wrapperStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...listeners}
      {...attributes}
    >
      {/* Inner wrapper that gets scaled - this is what makes borders/radius scale uniformly */}
      <div style={stampInnerStyle}>
        <Stamp
          config={stampConfig}
          documentIndex={documentIndex}
          caseData={caseData}
        />
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  return (
    prevProps.id === nextProps.id &&
    prevProps.basePosition.x === nextProps.basePosition.x &&
    prevProps.basePosition.y === nextProps.basePosition.y &&
    prevProps.documentIndex === nextProps.documentIndex &&
    prevProps.onCommitPosition === nextProps.onCommitPosition &&
    prevProps.pdfDisplayHeight === nextProps.pdfDisplayHeight &&
    JSON.stringify(prevProps.stampConfig) === JSON.stringify(nextProps.stampConfig) &&
    JSON.stringify(prevProps.caseData) === JSON.stringify(nextProps.caseData) &&
    JSON.stringify(prevProps.pdfFile?.pdfDimensions) === JSON.stringify(nextProps.pdfFile?.pdfDimensions)
  );
});

DraggableStamp.displayName = 'DraggableStamp';

const PdfViewer = React.memo(({ 
  pdfFile,
  onStampLocationUpdate,
  showStatus,
  stampConfig = {},
  caseData = null,
  documentIndex = 0,
  initialStampPosition = null,
  startPage = 1,
}) => {
  // Core state
  const [pdfDocument, setPdfDocument] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageViewport, setPageViewport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageRendered, setPageRendered] = useState(false);
  
  // NEW: Dynamic display height state
  const [pdfDisplayHeight, setPdfDisplayHeight] = useState(MIN_PDF_HEIGHT);

  // Stamp position state
  const [stampPositionPercent, setStampPositionPercent] = useState(
    initialStampPosition || getDefaultStampLocationPercent()
  );

  const { currentUser } = useAuth();

  // Refs
  const containerRef = useRef(null);
  const resizeObserverRef = useRef(null);
  
  // Document type determination
  const isIndexDocument = useMemo(() => 
    pdfFile?.isIndex || pdfFile?.id === 'INDEX_DOCUMENT', 
    [pdfFile?.isIndex, pdfFile?.id]
  );
  
  const shouldShowStamp = useMemo(() => 
    !isIndexDocument && pdfFile && (pdfFile.searchablePdfUrl || pdfFile.url), 
    [isIndexDocument, pdfFile?.searchablePdfUrl, pdfFile?.url]
  );

  // PDF URL
  const pdfUrl = pdfFile?.searchablePdfUrl;

  const calculateOptimalHeight = useCallback((containerWidth, containerHeight) => {
    if (!containerWidth || !containerHeight) {
      console.log('⚠️ Invalid container dimensions, using minimum height');
      return MIN_PDF_HEIGHT;
    }

    // Calculate max width and height based on container with padding
    const PADDING = 40; // 20px on each side
    const availableWidth = containerWidth - PADDING;
    const availableHeight = containerHeight - PADDING;

    // Calculate height constrained by width (maintaining letter aspect ratio)
    const heightFromWidth = availableWidth * LETTER_ASPECT_RATIO;
    
    // Use the smaller of the two constraints to ensure it fits
    const optimalHeight = Math.min(heightFromWidth, availableHeight);
    
    // Apply minimum constraint
    const finalHeight = Math.max(optimalHeight, MIN_PDF_HEIGHT);
    
    console.log('📐 Calculating optimal PDF height:', {
      containerWidth,
      containerHeight,
      availableWidth,
      availableHeight,
      heightFromWidth: heightFromWidth.toFixed(1),
      optimalHeight: optimalHeight.toFixed(1),
      finalHeight: finalHeight.toFixed(1),
      isWidthConstrained: heightFromWidth < availableHeight
    });
    
    return finalHeight;
  }, []);
  

  // Initial measurement on mount + ResizeObserver for dynamic updates
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      console.log('⚠️ Container not found on initial attempt, will retry on next render');
      return;
    }

    console.log('✅ Container found, setting up measurement and observer');

    // Inline calculation function (no dependency issues)
    const calculateHeight = (containerWidth, containerHeight) => {
      if (!containerWidth || !containerHeight) {
        console.log('⚠️ Invalid container dimensions');
        return MIN_PDF_HEIGHT;
      }

      const PADDING = 0;
      const availableWidth = containerWidth - PADDING;
      const availableHeight = containerHeight - PADDING;
      const heightFromWidth = availableWidth * LETTER_ASPECT_RATIO;
      const optimalHeight = Math.min(heightFromWidth, availableHeight);
      const finalHeight = Math.max(optimalHeight, MIN_PDF_HEIGHT);
      
      return finalHeight;
    };

    // Perform initial measurement immediately
    const rect = container.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      const initialHeight = calculateHeight(rect.width, rect.height);
      console.log('📏 Initial measurement:', {
        containerSize: { width: rect.width, height: rect.height },
        calculatedHeight: initialHeight.toFixed(1)
      });
      setPdfDisplayHeight(initialHeight);
    } else {
      console.log('⚠️ Container has zero dimensions on initial measurement');
    }

    // Create ResizeObserver for subsequent changes
    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        const newHeight = calculateHeight(width, height);
        
        setPdfDisplayHeight((prevHeight) => {
          if (Math.abs(newHeight - prevHeight) > 5) {
            console.log('🔄 ResizeObserver updating PDF display height:', {
              from: prevHeight.toFixed(1),
              to: newHeight.toFixed(1),
              containerSize: { width, height }
            });
            return newHeight;
          }
          return prevHeight;
        });
      }
    });

    resizeObserverRef.current.observe(container);
    console.log('👀 ResizeObserver connected and observing');

    // Cleanup
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        console.log('👋 ResizeObserver disconnected');
      }
    };
  }, [containerRef.current]); // Re-run when containerRef.current changes

  // Only update stamp position when initialStampPosition changes AND it's different
  useEffect(() => {
    if (initialStampPosition && 
        (initialStampPosition.x !== stampPositionPercent.x || 
        initialStampPosition.y !== stampPositionPercent.y)) {
      console.log('📍 Updating stamp position from parent:', initialStampPosition);
      setStampPositionPercent(initialStampPosition);
    }
  }, [initialStampPosition]);

  // Reset to zero when stamp shouldn't show
  useEffect(() => {
    if (!shouldShowStamp) {
      setStampPositionPercent({ x: 0, y: 0 });
    }
  }, [shouldShowStamp]);

  // Configure @dnd-kit sensors
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 3,
    },
  });
  
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 100,
      tolerance: 5,
    },
  });

  const sensors = useSensors(mouseSensor, touchSensor);

  // Handle loading state
  const handleLoadStart = useCallback(() => {
    setIsLoading(true);
    setError(null);
    setPageRendered(false);
  }, []);

  // Handle load error
  const handleLoadError = useCallback((error) => {
    console.error('PDF load error:', error);
    setError(error.message || 'Failed to load PDF');
    setIsLoading(false);
  }, []);

  // Handle successful PDF load
  const handleLoadSuccess = useCallback((pdf) => {
    console.log(`PDF loaded successfully: ${pdf.numPages} pages`);
    setPdfDocument(pdf);
    setNumPages(pdf.numPages);
    setIsLoading(false);
    setError(null);
  }, []);

  // Handle page render success
  const handlePageRenderSuccess = useCallback((page) => {
    console.log(`Page 1 rendered successfully`);
    
    try {
      const viewport = page.getViewport({ scale: 1 });
      setPageViewport(viewport);
      setPageRendered(true);
      
      console.log('✅ Page viewport set:', {
        width: viewport.width,
        height: viewport.height,
        scale: viewport.scale
      });
    } catch (error) {
      console.error('❌ Error in handlePageRenderSuccess:', error);
    }
  }, []);

  // Debug function for container bounds
  const debugContainerBounds = useCallback(() => {
    const stampContainer = containerRef.current?.querySelector('.stamp-container');
    const page = containerRef.current?.querySelector('.react-pdf__Page');
    
    if (stampContainer && page) {
      const containerRect = stampContainer.getBoundingClientRect();
      const pageRect = page.getBoundingClientRect();
      
      console.log('📦 Container bounds check:', {
        containerSize: { width: containerRect.width, height: containerRect.height },
        pageSize: { width: pageRect.width, height: pageRect.height },
        matches: Math.abs(containerRect.width - pageRect.width) < 1 && 
                 Math.abs(containerRect.height - pageRect.height) < 1
      });
    }
  }, []);

  // Debug useEffect
  useEffect(() => {
    if (pageRendered) {
      debugContainerBounds();
    }
  }, [pageRendered, debugContainerBounds]);

  // Update local state and notify parent
  const handleCommitStampPosition = useCallback((newPercent) => {
    console.log('💾 Committing new stamp position:', newPercent);
    
    // Update local state immediately
    setStampPositionPercent(newPercent);
    
    // Notify parent (EvidenceManager) - it will handle the database save
    if (onStampLocationUpdate) {
      onStampLocationUpdate(newPercent);
    }
  }, [onStampLocationUpdate]);

  const handleDragEnd = useCallback((event) => {
    console.log('🎯 Drag ended:', event);
    
    const { delta } = event;
    
    if (!delta || (!delta.x && !delta.y)) {
      console.log('📌 No movement detected, not updating position');
      return;
    }

    if (currentUser?.uid) {
      incrementActionCount(currentUser.uid, 'moveStamp');
    }

    // Find the stamp container - this is our reference for percentage calculations
    const stampContainer = containerRef.current?.querySelector('.stamp-container');
    
    if (!stampContainer) {
      console.error('❌ Could not find .stamp-container for position calculation');
      return;
    }
    
    const containerRect = stampContainer.getBoundingClientRect();
    
    // Convert pixel delta to percentage delta
    const deltaPercentX = (delta.x / containerRect.width) * 100;
    const deltaPercentY = (delta.y / containerRect.height) * 100;
    
    // Calculate new position
    const newPosition = {
      x: stampPositionPercent.x + deltaPercentX,
      y: stampPositionPercent.y + deltaPercentY
    };
    
    // Optional: Clamp to bounds (0 to 100% minus stamp size)
    const maxPercentX = 100 - ((STAMP_DIMENSIONS.BASE_WIDTH / pdfFile.pdfDimensions.width) * 100);
    const maxPercentY = 100 - ((STAMP_DIMENSIONS.BASE_HEIGHT / pdfFile.pdfDimensions.height) * 100);
    
    const clampedPosition = {
      x: Math.max(0, Math.min(newPosition.x, maxPercentX)),
      y: Math.max(0, Math.min(newPosition.y, maxPercentY))
    };
    
    console.log('📍 Position calculation:', {
      oldPercent: { x: stampPositionPercent.x.toFixed(2), y: stampPositionPercent.y.toFixed(2) },
      deltaPixels: { x: delta.x, y: delta.y },
      deltaPercent: { x: deltaPercentX.toFixed(2), y: deltaPercentY.toFixed(2) },
      newPercent: { x: newPosition.x.toFixed(2), y: newPosition.y.toFixed(2) },
      clampedPercent: { x: clampedPosition.x.toFixed(2), y: clampedPosition.y.toFixed(2) },
      containerSize: { width: containerRect.width, height: containerRect.height }
    });
    
    handleCommitStampPosition(clampedPosition);
    
  }, [stampPositionPercent, handleCommitStampPosition, pdfFile?.pdfDimensions]);

  // Determine if we should show stamp on this page
  const showStampOnPage = useMemo(() => 
    shouldShowStamp && pageRendered && stampPositionPercent.x !== 0,
    [shouldShowStamp, pageRendered, stampPositionPercent.x]
  );

  const renderAllPages = useCallback(() => {
    if (!numPages) return null;

    const pageNumbers = Array.from({ length: numPages }, (_, i) => i + 1);

    return (
      <DndContext
        sensors={sensors}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToParentElement]}
      >
        {pageNumbers.map((pageNumber) => (
          <div
            key={pageNumber}
            style={{
              marginBottom: pageNumber < numPages ? '20px' : '0'
            }}
          >
            <div className="relative inline-block bg-white shadow-lg border border-app-gray-900">
              <Page
                key={`page_${pageNumber}`}
                pageNumber={pageNumber}
                height={pdfDisplayHeight}
                onRenderSuccess={pageNumber === 1 && !pageRendered ? handlePageRenderSuccess : undefined}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
              
              {/* Stamp container - only on first page */}
              {pageNumber === 1 && showStampOnPage && (
                <div className="stamp-container" style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none'
                }}>
                  <DraggableStamp
                    id={`stamp-${pdfFile.id}`}
                    basePosition={stampPositionPercent}
                    stampConfig={stampConfig}
                    documentIndex={documentIndex}
                    caseData={caseData}
                    onCommitPosition={handleCommitStampPosition}
                    pdfFile={pdfFile}
                    pdfDisplayHeight={pdfDisplayHeight}
                  />
                </div>
              )}
              {/* Page number - on ALL pages (not just first) */}
              {!isIndexDocument && (
                <PageNumber 
                  pageNumber={startPage + (pageNumber - 1)} 
                  pdfScale={pdfFile?.pdfDimensions?.height ? pdfDisplayHeight / pdfFile.pdfDimensions.height : 1}
                />
              )}
            </div>
          </div>
        ))}
      </DndContext>
    );
  }, [
    numPages, 
    sensors, 
    handleDragEnd, 
    pdfDisplayHeight, 
    pageRendered, 
    handlePageRenderSuccess,
    showStampOnPage,
    pdfFile,
    stampPositionPercent,
    stampConfig,
    documentIndex,
    caseData,
    handleCommitStampPosition
  ]);

  // PROCESSING STATE: Show skeleton if file is still processing
  const isProcessing = pdfFile?.showSpinner ||
                      pdfFile?.status === 'processing' ||
                      pdfFile?.status !== 'completed' ||
                      !pdfUrl;
  
  if (isProcessing) {
    return <PdfSkeleton documentName={pdfFile?.name || 'Document'} />;
  }

  // Render loading/error states or PDF
  if (!pdfFile || !pdfUrl) {
    return (
      <div className="h-full flex items-center justify-center p-5">
        <div className="text-center text-gray-500">
          No PDF available
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center p-5">
        <div className="text-center text-red-600">
          <p className="font-semibold mb-2">Error loading PDF</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef} 
      className="h-full w-full flex items-start justify-center p-5"
      style={{ 
        minWidth: `${MIN_PDF_WIDTH + 40}px`,  // PDF min width + padding (20px each side)
        minHeight: `${MIN_PDF_HEIGHT + 40}px`, // PDF min height + padding (20px each side)
      }}
    >
    {isLoading && !pageRendered && (
      <PdfSkeleton 
        documentName={pdfFile.name} 
        displayHeight={pdfDisplayHeight}  // <- Make sure this line is there
      />
    )}
      
      {/* CSS safety net: constrain max-width to prevent overflow from stale ResizeObserver values */}
      <div 
        style={{ 
          display: isLoading && !pageRendered ? 'none' : 'block',
          maxWidth: '100%',  // Never exceed container width
          width: 'fit-content' // Shrink to PDF's actual size
        }}
      >
        <Document
          file={pdfUrl}
          onLoadStart={handleLoadStart}
          onLoadSuccess={handleLoadSuccess}
          onLoadError={handleLoadError}
          loading={null}
        >
          {renderAllPages()}
        </Document>
      </div>
    </div>
  );
});

PdfViewer.displayName = 'PdfViewer';

export default PdfViewer;